import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BookOpen, Calendar, Eye, Tag, ChevronRight, Search, 
  Plus, Edit, Trash2, X, Save, ArrowLeft, User
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogHub = ({ token, isAdmin = false }) => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, detail, editor
  const [editingPost, setEditingPost] = useState(null);
  
  // Editor state
  const [postForm, setPostForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'nutrition',
    tags: [],
    image_url: '',
    published: true
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchPosts();
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/blog/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      
      const response = await axios.get(`${API}/blog/posts?${params.toString()}`);
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPostDetail = async (slug) => {
    try {
      const response = await axios.get(`${API}/blog/posts/${slug}`);
      setSelectedPost(response.data);
      setView('detail');
    } catch (error) {
      console.error('Error fetching post:', error);
    }
  };

  const handleCreatePost = async () => {
    if (!postForm.title || !postForm.content) {
      alert('Title and content are required');
      return;
    }
    
    setSaving(true);
    try {
      const slug = postForm.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      
      await axios.post(`${API}/blog/posts`, {
        ...postForm,
        slug
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Post created successfully!');
      setView('list');
      setPostForm({
        title: '',
        excerpt: '',
        content: '',
        category: 'nutrition',
        tags: [],
        image_url: '',
        published: true
      });
      fetchPosts();
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Failed to create post');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePost = async () => {
    if (!editingPost) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/blog/posts/${editingPost.id}`, postForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Post updated successfully!');
      setView('list');
      setEditingPost(null);
      fetchPosts();
    } catch (error) {
      console.error('Error updating post:', error);
      alert('Failed to update post');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${API}/blog/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Post deleted successfully!');
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post');
    }
  };

  const startEditing = (post) => {
    setEditingPost(post);
    setPostForm({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      tags: post.tags || [],
      image_url: post.image_url || '',
      published: post.published
    });
    setView('editor');
  };

  const addTag = () => {
    if (tagInput.trim() && !postForm.tags.includes(tagInput.trim())) {
      setPostForm({ ...postForm, tags: [...postForm.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setPostForm({ ...postForm, tags: postForm.tags.filter(t => t !== tag) });
  };

  const filteredPosts = posts.filter(post => 
    post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Post Detail View
  if (view === 'detail' && selectedPost) {
    return (
      <div className="max-w-4xl mx-auto">
        <button 
          onClick={() => { setView('list'); setSelectedPost(null); }}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-[#FF6600] mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Blog
        </button>
        
        {selectedPost.image_url && (
          <img 
            src={selectedPost.image_url} 
            alt={selectedPost.title}
            className="w-full h-64 object-cover rounded-xl mb-6"
          />
        )}
        
        <div className="mb-4">
          <span className="px-3 py-1 bg-[#FF6600]/10 text-[#FF6600] rounded-full text-sm font-medium">
            {categories.find(c => c.id === selectedPost.category)?.name || selectedPost.category}
          </span>
        </div>
        
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">{selectedPost.title}</h1>
        
        <div className="flex items-center gap-6 text-gray-500 dark:text-gray-400 mb-8">
          <div className="flex items-center gap-2">
            <User size={18} />
            <span>{selectedPost.author}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar size={18} />
            <span>{formatDate(selectedPost.created_at)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Eye size={18} />
            <span>{selectedPost.views || 0} views</span>
          </div>
        </div>
        
        <div className="prose prose-lg max-w-none">
          <div 
            className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: selectedPost.content.replace(/\n/g, '<br/>') }}
          />
        </div>
        
        {selectedPost.tags?.length > 0 && (
          <div className="mt-8 pt-6 border-t">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag size={18} className="text-gray-400" />
              {selectedPost.tags.map((tag, i) => (
                <span key={i} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full text-sm">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Editor View
  if (view === 'editor') {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => { setView('list'); setEditingPost(null); }}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-[#FF6600] transition-colors"
          >
            <ArrowLeft size={20} />
            Cancel
          </button>
          <h2 className="text-2xl font-bold">
            {editingPost ? 'Edit Post' : 'Create New Post'}
          </h2>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Title *</label>
            <input
              type="text"
              value={postForm.title}
              onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
              placeholder="Enter post title..."
              className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-[#FF6600] focus:ring-2 focus:ring-[#FF6600]/20"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Excerpt</label>
            <textarea
              value={postForm.excerpt}
              onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })}
              placeholder="Brief summary of the post..."
              rows={2}
              className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-[#FF6600] focus:ring-2 focus:ring-[#FF6600]/20"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Content *</label>
            <textarea
              value={postForm.content}
              onChange={(e) => setPostForm({ ...postForm, content: e.target.value })}
              placeholder="Write your blog post content here..."
              rows={15}
              className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-[#FF6600] focus:ring-2 focus:ring-[#FF6600]/20 font-mono text-sm"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Category</label>
              <select
                value={postForm.category}
                onChange={(e) => setPostForm({ ...postForm, category: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-[#FF6600]"
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Image URL</label>
              <input
                type="text"
                value={postForm.image_url}
                onChange={(e) => setPostForm({ ...postForm, image_url: e.target.value })}
                placeholder="https://example.com/image.jpg"
                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-[#FF6600]"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Tags</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder="Add a tag..."
                className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:border-[#FF6600]"
              />
              <button onClick={addTag} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200">
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {postForm.tags.map((tag, i) => (
                <span key={i} className="flex items-center gap-1 px-3 py-1 bg-[#FF6600]/10 text-[#FF6600] rounded-full text-sm">
                  {tag}
                  <X size={14} className="cursor-pointer" onClick={() => removeTag(tag)} />
                </span>
              ))}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="published"
              checked={postForm.published}
              onChange={(e) => setPostForm({ ...postForm, published: e.target.checked })}
              className="w-5 h-5 text-[#FF6600] rounded"
            />
            <label htmlFor="published" className="font-medium">Publish immediately</label>
          </div>
          
          <div className="flex gap-4 pt-4">
            <button
              onClick={editingPost ? handleUpdatePost : handleCreatePost}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#FF6600] text-white font-semibold rounded-lg hover:bg-[#E55C00] disabled:opacity-50 transition-colors"
            >
              <Save size={20} />
              {saving ? 'Saving...' : (editingPost ? 'Update Post' : 'Publish Post')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <BookOpen className="text-[#FF6600]" />
            Nutrifitness Blog
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Health tips, nutrition advice, and success stories</p>
        </div>
        {token && (
          <button
            onClick={() => { setView('editor'); setEditingPost(null); setPostForm({
              title: '', excerpt: '', content: '', category: 'nutrition', tags: [], image_url: '', published: true
            }); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#FF6600] text-white rounded-lg hover:bg-[#E55C00] transition-colors"
          >
            <Plus size={20} />
            Write Post
          </button>
        )}
      </div>

      {/* Search & Categories */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search articles..."
            className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:border-[#FF6600] focus:ring-2 focus:ring-[#FF6600]/20"
          />
        </div>
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`px-4 py-2 rounded-full font-medium transition-colors ${
            !selectedCategory ? 'bg-[#FF6600] text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
          }`}
        >
          All Posts
        </button>
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-4 py-2 rounded-full font-medium transition-colors ${
              selectedCategory === cat.id ? 'bg-[#FF6600] text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
            }`}
          >
            {cat.icon} {cat.name}
          </button>
        ))}
      </div>

      {/* Posts Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-[#FF6600] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-gray-500 dark:text-gray-400 mt-4">Loading posts...</p>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-xl">
          <BookOpen size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-300 mb-2">No posts yet</h3>
          <p className="text-gray-500 dark:text-gray-400">Be the first to write a post!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post) => (
            <article 
              key={post.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
              onClick={() => fetchPostDetail(post.slug)}
            >
              {post.image_url ? (
                <img 
                  src={post.image_url} 
                  alt={post.title}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              ) : (
                <div className="w-full h-48 bg-gradient-to-br from-[#FF6600] to-[#FF8533] flex items-center justify-center">
                  <BookOpen size={48} className="text-white/50" />
                </div>
              )}
              
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2 py-1 bg-[#FF6600]/10 text-[#FF6600] rounded text-xs font-medium">
                    {categories.find(c => c.id === post.category)?.icon} {categories.find(c => c.id === post.category)?.name || post.category}
                  </span>
                </div>
                
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-[#FF6600] transition-colors">
                  {post.title}
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-2">
                  {post.excerpt || post.content?.substring(0, 120) + '...'}
                </p>
                
                <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-2">
                    <Calendar size={14} />
                    <span>{formatDate(post.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1 text-[#FF6600] font-medium">
                    Read more
                    <ChevronRight size={16} />
                  </div>
                </div>
              </div>
              
              {/* Admin Actions */}
              {token && (
                <div className="px-5 pb-4 flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => startEditing(post)}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                  >
                    <Edit size={14} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeletePost(post.id)}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogHub;
